/**
 * An edit card's changes, exactly as the Word redline marks them.
 *
 * The backend sends them with each edit suggestion (cardChangeTokens in
 * backend/src/api/qagent/word-diff.ts): unchanged and deleted text as a
 * number of tokens, inserted text as is. The viewer applies them to its own
 * copy of the document text, so the preview shows the same changes as the
 * downloaded redline, and unchanged text keeps the document's characters
 * (a tab, curly quotes) even where the suggestion has a space or straight
 * quotes.
 */

export type CardChange =
  | { type: 'equal' | 'delete'; tokens: number }
  | { type: 'insert'; text: string };

export type ChangeRange =
  | { type: 'delete'; start: number; end: number }
  | { type: 'insert'; at: number; text: string };

/** Same tokens as the backend's tokenize: whitespace runs, words, single symbols */
const TOKEN = /\s+|[\p{L}\p{N}]+(?:['’.,-][\p{L}\p{N}]+)*|[^\s\p{L}\p{N}]/gu;

export function tokenize(text: string): string[] {
  return text.match(TOKEN) ?? [];
}

/**
 * Straight quotes in new text as the curly quotes the document uses, only
 * when the original text has curly quotes and no straight ones. Same rule
 * as the backend's matchQuoteStyle; the redline applies it to the document
 * text, so the viewer does too (the suggestion's copy of the text may have
 * straight quotes where the document has curly ones).
 */
export function matchQuoteStyle(oldText: string, newText: string): string {
  const curly = (open: string, close: string, straight: string) =>
    oldText.includes(open) || oldText.includes(close) ? !oldText.includes(straight) : false;
  let text = newText;
  if (curly('“', '”', '"')) {
    const source = text;
    text = source.replace(/"/g, (_, i: number) => (i === 0 || /[\s([{\u2014-]/.test(source[i - 1]) ? '“' : '”'));
  }
  if (curly('‘', '’', "'")) {
    const source = text;
    text = source.replace(/'/g, (_, i: number) => (i === 0 || /[\s([{\u2014-]/.test(source[i - 1]) ? '‘' : '’'));
  }
  return text;
}

/**
 * Character ranges of the changes within `text` (the document's copy of the
 * suggestion's old text), in order. Null when the changes don't fit the text
 * (a different number of tokens), so the caller can fall back.
 */
export function changeRanges(text: string, changes: CardChange[]): ChangeRange[] | null {
  const tokens = tokenize(text);
  const counted = changes.reduce((n, c) => n + ('tokens' in c ? c.tokens : 0), 0);
  if (counted !== tokens.length || tokens.join('') !== text) return null;

  const offsets: number[] = [0];
  for (const t of tokens) offsets.push(offsets[offsets.length - 1] + t.length);

  // The new wording in the document's quote style, decided on the whole new
  // text as the redline does (a fragment alone would lose its context)
  let newText = '';
  const insertAt: number[] = [];
  let token = 0;
  for (const change of changes) {
    if (change.type === 'insert') {
      insertAt.push(newText.length);
      newText += change.text;
    } else {
      const next = token + change.tokens;
      if (change.type === 'equal') newText += text.slice(offsets[token], offsets[next]);
      token = next;
    }
  }
  const styled = matchQuoteStyle(text, newText);

  const ranges: ChangeRange[] = [];
  let insertIndex = 0;
  token = 0;
  for (const change of changes) {
    if (change.type === 'insert') {
      const at = insertAt[insertIndex++];
      const inserted = styled.slice(at, at + change.text.length);
      if (inserted) ranges.push({ type: 'insert', at: offsets[token], text: inserted });
      continue;
    }
    const next = token + change.tokens;
    if (change.type === 'delete' && next > token) {
      ranges.push({ type: 'delete', start: offsets[token], end: offsets[next] });
    }
    token = next;
  }
  return ranges;
}

/**
 * Inserted wording formatted the way the document formats it by hand, as the
 * redline does (insertRoles in backend/src/api/qagent/word-diff.ts): a new
 * clause heading ("Group Use.") like the heading it replaces, quoted defined
 * terms ("AL Companies") like the paragraph's other defined terms, the rest
 * like the body text.
 */
export type InsertRole = 'heading' | 'term' | 'body';

/** A clause heading run on its own: "Affiliates.", "Price Adjustment." */
export function isClauseHeading(text: string): boolean {
  const t = text.trim();
  return /^[^\s.][^.\n]{0,79}\.$/.test(t) && t.split(/\s+/).length <= 6;
}

/** Whether text[start, end) is a defined term between quotes: (“Client”) */
export function isQuotedTerm(text: string, start: number, end: number): boolean {
  return (
    start > 0 &&
    /[“"]/.test(text[start - 1]) &&
    /[”"]/.test(text[end] ?? '') &&
    /^[^“”"\n]{1,60}$/.test(text.slice(start, end))
  );
}

// A title-case heading at the start: "Group Use.", "Limitation of Liability."
const HEADING_WORD = "[\\p{Lu}\\p{N}][\\p{L}\\p{N}&'’-]*";
const HEADING_START = new RegExp(
  `^(\\s*)(${HEADING_WORD}(?:\\s+(?:${HEADING_WORD}|and|of|or|the|to|for|in|on|&)){0,5}\\.)(?=\\s|$)`,
  'u',
);
const QUOTED_TERM = /[“"]([^“”"\n]{1,60})[”"]/g;

export function insertRoles(
  text: string,
  available: { heading: boolean; term: boolean },
): Array<{ text: string; role: InsertRole }> {
  const marks: Array<{ start: number; end: number; role: InsertRole }> = [];
  const heading = available.heading ? HEADING_START.exec(text) : null;
  if (heading) marks.push({ start: heading[1].length, end: heading[0].length, role: 'heading' });
  if (available.term) {
    for (const m of Array.from(text.matchAll(QUOTED_TERM))) {
      const start = m.index! + 1;
      if (heading && start < heading[0].length) continue;
      marks.push({ start, end: start + m[1].length, role: 'term' });
    }
  }
  const pieces: Array<{ text: string; role: InsertRole }> = [];
  let pos = 0;
  for (const mark of marks) {
    if (mark.start > pos) pieces.push({ text: text.slice(pos, mark.start), role: 'body' });
    pieces.push({ text: text.slice(mark.start, mark.end), role: mark.role });
    pos = mark.end;
  }
  if (pos < text.length) pieces.push({ text: text.slice(pos), role: 'body' });
  return pieces;
}
